import { supabase } from '../utils/supabase';

export interface UserData {
  id: string;
  phone: string;
  user_type: 'admin' | 'user';
  is_active: boolean;
  created_at: string;
}

export interface CreateUserRequest {
  phone: string;
  password: string;
  user_type: 'admin' | 'user';
}

export interface CreateUserResponse {
  success: boolean;
  user_id?: string;
  message: string;
  error?: string;
}

export interface ListUsersResponse {
  success: boolean;
  users?: UserData[];
  message?: string;
  error?: string;
}

export interface ToggleUserStatusResponse {
  success: boolean;
  message: string;
  error?: string;
}

export interface ResetPasswordRequest {
  newPassword: string;
}

export interface ResetPasswordResponse {
  success: boolean;
  message: string;
  error?: string;
}

class UserService {
  /**
   * Create a new user (Admin only)
   */
  async createUser(adminUserId: string, userData: CreateUserRequest): Promise<CreateUserResponse> {
    try {
      if (!supabase) {
        return {
          success: false,
          message: 'Database connection not available',
          error: 'SUPABASE_NOT_CONFIGURED'
        };
      }

      console.log('🔧 Creating user with data:', { 
        admin_user_id: adminUserId, 
        phone: userData.phone, 
        user_type: userData.user_type 
      });

      const { data, error } = await supabase.rpc('create_user', {
        p_admin_user_id: adminUserId,
        p_phone: userData.phone,
        p_password: userData.password,
        p_user_type: userData.user_type
      });

      if (error) {
        console.error('❌ Create user RPC error:', error);
        return {
          success: false,
          message: error.message || 'Failed to create user',
          error: error.code || 'RPC_ERROR'
        };
      }

      console.log('✅ User created successfully:', data);

      // RPC functions return arrays, so get the first element
      const result = Array.isArray(data) ? data[0] : data;

      if (result?.success) {
        return {
          success: true,
          user_id: result.user_id,
          message: result.message || 'User created successfully'
        };
      } else {
        return {
          success: false,
          message: result?.message || 'Failed to create user',
          error: 'CREATE_FAILED'
        };
      }
    } catch (error) {
      console.error('❌ Create user error:', error);
      return {
        success: false,
        message: 'An unexpected error occurred while creating user',
        error: 'UNEXPECTED_ERROR'
      };
    }
  }

  /**
   * List all users (Admin only)
   */
  async listUsers(adminUserId: string): Promise<ListUsersResponse> {
    try {
      if (!supabase) {
        return {
          success: false,
          message: 'Database connection not available',
          error: 'SUPABASE_NOT_CONFIGURED'
        };
      }

      console.log('🔧 Fetching users list for admin:', adminUserId);

      const { data, error } = await supabase.rpc('list_users', {
        p_admin_user_id: adminUserId
      });

      if (error) {
        console.error('❌ List users RPC error:', error);
        return {
          success: false,
          message: error.message || 'Failed to fetch users',
          error: error.code || 'RPC_ERROR'
        };
      }

      console.log('✅ Users fetched successfully:', data);

      // The RPC function returns an array directly
      if (Array.isArray(data)) {
        return {
          success: true,
          users: data,
          message: 'Users fetched successfully'
        };
      } else {
        return {
          success: false,
          message: 'Invalid response format',
          error: 'INVALID_RESPONSE'
        };
      }
    } catch (error) {
      console.error('❌ List users error:', error);
      return {
        success: false,
        message: 'An unexpected error occurred while fetching users',
        error: 'UNEXPECTED_ERROR'
      };
    }
  }

  /**
   * Toggle user active status (Admin only)
   */
  async toggleUserStatus(adminUserId: string, targetUserId: string): Promise<ToggleUserStatusResponse> {
    try {
      if (!supabase) {
        return {
          success: false,
          message: 'Database connection not available',
          error: 'SUPABASE_NOT_CONFIGURED'
        };
      }

      console.log('🔧 Toggling user status:', { admin_user_id: adminUserId, target_user_id: targetUserId });

      const { data, error } = await supabase.rpc('toggle_user_status', {
        p_admin_user_id: adminUserId,
        p_target_user_id: targetUserId
      });

      if (error) {
        console.error('❌ Toggle user status RPC error:', error);
        return {
          success: false,
          message: error.message || 'Failed to toggle user status',
          error: error.code || 'RPC_ERROR'
        };
      }

      console.log('✅ User status toggled successfully:', data);

      // RPC functions return arrays, so get the first element
      const result = Array.isArray(data) ? data[0] : data;

      if (result?.success) {
        return {
          success: true,
          message: result.message || 'User status updated successfully'
        };
      } else {
        return {
          success: false,
          message: result?.message || 'Failed to toggle user status',
          error: 'TOGGLE_FAILED'
        };
      }
    } catch (error) {
      console.error('❌ Toggle user status error:', error);
      return {
        success: false,
        message: 'An unexpected error occurred while updating user status',
        error: 'UNEXPECTED_ERROR'
      };
    }
  }

  /**
   * Reset user password (Admin only)
   */
  async resetUserPassword(adminUserId: string, targetUserId: string, newPassword: string): Promise<ResetPasswordResponse> {
    try {
      if (!supabase) {
        return {
          success: false,
          message: 'Database connection not available',
          error: 'SUPABASE_NOT_CONFIGURED'
        };
      }

      console.log('🔧 Resetting password for user:', targetUserId);

      const { data, error } = await supabase.rpc('admin_reset_user_password', {
        p_admin_user_id: adminUserId,
        p_target_user_id: targetUserId,
        p_new_password: newPassword
      });

      if (error) {
        console.error('❌ Reset password RPC error:', error);
        return {
          success: false,
          message: error.message || 'Failed to reset password',
          error: error.code || 'RPC_ERROR'
        };
      }

      console.log('✅ Password reset result:', data);

      // RPC functions return arrays, so get the first element
      const result = Array.isArray(data) ? data[0] : data;

      if (result?.success) {
        return {
          success: true,
          message: result.message || 'Password reset successfully'
        };
      } else {
        return {
          success: false,
          message: result?.message || 'Failed to reset password',
          error: 'RESET_FAILED'
        };
      }
    } catch (error) {
      console.error('❌ Reset password error:', error);
      return {
        success: false,
        message: 'An unexpected error occurred while resetting password',
        error: 'UNEXPECTED_ERROR'
      };
    }
  }

  /**
   * Get user profile (for validation/display)
   */
  async getUserProfile(userId: string): Promise<{ success: boolean; user?: UserData; message?: string; error?: string }> {
    try {
      if (!supabase) {
        return {
          success: false,
          message: 'Database connection not available',
          error: 'SUPABASE_NOT_CONFIGURED'
        };
      }

      console.log('🔧 Fetching user profile for:', userId);

      const { data, error } = await supabase.rpc('get_user_profile', {
        p_user_id: userId
      });

      if (error) {
        console.error('❌ Get user profile RPC error:', error);
        return {
          success: false,
          message: error.message || 'Failed to fetch user profile',
          error: error.code || 'RPC_ERROR'
        };
      }

      console.log('✅ User profile fetched successfully:', data);

      // RPC functions return arrays, so get the first element
      const result = Array.isArray(data) ? data[0] : data;

      if (result) {
        return {
          success: true,
          user: result,
          message: 'User profile fetched successfully'
        };
      } else {
        return {
          success: false,
          message: 'User not found',
          error: 'USER_NOT_FOUND'
        };
      }
    } catch (error) {
      console.error('❌ Get user profile error:', error);
      return {
        success: false,
        message: 'An unexpected error occurred while fetching user profile',
        error: 'UNEXPECTED_ERROR'
      };
    }
  }
}

export const userService = new UserService();
export default userService;
